'use client';

import React from 'react';

import { Body } from '@depop/web-thrift-ui/Typography';
import { CheckmarkIcon } from '@depop/web-ui-kit/Icons/CheckmarkIcon';
import { useTranslations } from 'next-intl';

import styles from './styles.module.css';

import { ButtonExperiment } from '@/components/ButtonExperiment';
import { useButtonPlaceholderHeight } from '@/components/ButtonExperiment/useButtonPlaceholderHeight';
import { ButtonWithError } from '@/components/ButtonWithError';
import { Placeholder, PlaceHolderShape } from '@/components/Placeholder';
import { createTrackingId } from '@/modules/activityTracker/useActivityTracker';
import { ADD_TO_BAG_EVENT_NAME } from '@/modules/bag/constants';
import { useAddToBag } from '@/modules/bag/useAddToBag';
import { useBagBasic } from '@/modules/bag/useBagBasic';
import { useBagTracking } from '@/modules/bag/useBagTracking';
import { type CurrencyCode } from '@/modules/location/constants';
import { isProductOnSale } from '@/modules/product/helpers/isProductOnSale';
import { useProductBySlugBasicPDP } from '@/modules/product/hooks/useProductBySlugBasicPDP';
import { useProductBySlugExtendedPDP } from '@/modules/product/hooks/useProductBySlugExtendedPDP';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type ButtonProps = React.ComponentProps<typeof ButtonExperiment>;

export type Props = {
  hasMultipleSizes: boolean;
  className?: string;
  selectedVariantId?: number;
  preventBuy: boolean;
} & Pick<ButtonProps, 'size' | 'variant'>;

export function AddToBagButton({
  hasMultipleSizes,
  className,
  selectedVariantId,
  preventBuy,
  size,
  variant = 'secondary',
}: Props) {
  const { data: product, isLoading: productLoading } =
    useProductBySlugBasicPDP();
  const { data: productExtended, isLoading: productExtendedLoading } =
    useProductBySlugExtendedPDP();
  const t = useTranslations('bag');
  const [authenticated] = useCurrentUser();
  const currency = productExtended?.pricing.currency;
  const productId = product?.id;
  const sellerId = product?.seller?.id;
  const status = product?.status;
  const pricing = productExtended?.pricing;

  const { query: basicBagQuery } = useBagBasic();

  const bagTracking = useBagTracking();

  const { addToBag, errorText } = useAddToBag(currency as CurrencyCode);

  const placeholderHeight = useButtonPlaceholderHeight(size);

  if (!isProductOnSale(status)) {
    return null;
  }

  const productMatch = productId
    ? basicBagQuery.data?.products[productId]
    : undefined;

  const inBag = hasMultipleSizes
    ? Boolean(
        productMatch?.find(
          (product) =>
            product.product_id === productId &&
            product.variant_id === selectedVariantId
        )
      )
    : Boolean(productMatch);

  const products = basicBagQuery.data?.products || {};

  function sendTracking() {
    const bagProducts = Object.values(products).reduce<{
      ids: number[];
      variantIds: Array<number | null>;
    }>(
      (bagProducts, bagProduct) => {
        return {
          ids: [
            ...bagProducts.ids,
            ...bagProduct.map((basicProduct) => basicProduct.product_id),
          ],
          variantIds: [
            ...bagProducts.variantIds,
            ...bagProduct.map(
              (basicProduct) => basicProduct.variant_id || null
            ),
          ],
        };
      },
      { ids: [], variantIds: [] }
    );

    const eventId = createTrackingId();

    if (productId) {
      bagTracking.sendAddItemToBagAction({
        baggedProductIds: [...bagProducts.ids, productId],
        baggedProductVariants: [
          ...bagProducts.variantIds,
          selectedVariantId || null,
        ],
        productIds: [productId],
        eventId,
      });
      if (pricing) {
        bagTracking.sendAddToBagGTMEvent({
          id: String(productId),
          currency: pricing.currency,
          name: product.description || '',
          price: String(
            parseFloat(pricing.original_price.price_breakdown.price.amount)
          ),
          value: String(
            parseFloat(pricing.original_price.price_breakdown.price.amount)
          ),
          ...(product.attributes.product_type && {
            item_category: product.attributes.product_type,
          }),
          variantId: selectedVariantId,
          eventId,
        });
      }
    }
  }

  function handleAddToBagClick() {
    sendTracking();
    document.dispatchEvent(new CustomEvent(ADD_TO_BAG_EVENT_NAME));

    if (productId && sellerId) {
      addToBag([
        {
          product_id: productId,
          variant_id: selectedVariantId,
          quantity: 1,
          seller_id: sellerId,
        },
      ]);
    }
  }

  function renderContent() {
    if (inBag) {
      return (
        <div className={styles.addedWrapper}>
          <CheckmarkIcon title="tick" />
          <Body variant="sm-bold">{t('AddToBag.Added')}</Body>
        </div>
      );
    }
    return (
      <ButtonExperiment
        legacyComponent={ButtonWithError}
        fullWidth
        variant={variant}
        disabled={preventBuy}
        onClick={handleAddToBagClick}
        size={size}
        errorText={errorText}
      >
        {t('AddToBag.Button')}
      </ButtonExperiment>
    );
  }

  const bagBasicLoading =
    basicBagQuery.isLoading || (authenticated && !basicBagQuery.isFetched);
  const isLoading = productLoading || productExtendedLoading || bagBasicLoading;

  return (
    <div className={className}>
      {isLoading ? (
        <Placeholder
          className={styles.placeholder}
          shape={PlaceHolderShape.RECTANGLE}
          width="100%"
          height={placeholderHeight}
        />
      ) : (
        renderContent()
      )}
    </div>
  );
}
